import React, { useEffect, useState } from 'react';
import { useStore } from 'effector-react';
import { usePageTitle } from '@app/hooks';
import { taskStateColumns } from './consts';
import { $taskKanbanBoard, $taskKanbanBoardFormatted } from './store/states';
import { fetchTaskKanbanBoard } from './store/effects';
import { taskKanbanBoardFormattedApi } from './store/apis';
import PageContainer from '@components/PageContainer';
import Flex from '@components/Flex';
import NonIdealState from '@components/NonIdealState';
import TaskKanbanColumn from './components/TaskKanbanColumn';
import TaskKanbanTeamsFilter from './components/TaskKanbanTeamsFilter';
import TaskKanbanClientsFilter from './components/TaskKanbanClientsFilter';
import { ScrollContainerWrapper, StyledScrollContainer } from './styled';
import { Button, ButtonGroup, Colors, Divider, Icon, Spinner } from '@blueprintjs/core';
import Heading from '@components/Heading';
import { TaskQueue } from 'dy-frontend-http-repository/lib/data/enums';
import { taskStateInformation } from '@app/data/consts';
import { TaskKanbanBoardFilterInput } from './types';
import { $authorizedUser } from '@app/containers/store/states';
import { TaskKanbanFilterType } from './enums';
import { LocalStorage, SessionStorage } from '@app/data/enums';
import { parseStoredTaskKanbanFilter, serializeTaskKanbanFilter } from './utils';
import DevText from '@app/components/Text';
import useTimeout from '@app/hooks/useTimeout';
import { kanbanMyTeamDefaultScopeRoles, kanbanMyTeamFilterRoles } from '@data/consts/roleFeatureAccess';


const TaskKanbanBoard: React.FC = () => {
    // TODO: check that all pages use usePageTitle hook
    usePageTitle('Kanban Board');
    const { isTimeoutPresent, handleStartTimeout } = useTimeout({
        timeoutKey: LocalStorage.REFRESH_TASK_KANBAN_BOARD,
        timeoutSeconds: 10,
        labelUpdateTimeoutSeconds: 1,
    });

    const me = useStore($authorizedUser);
    const taskKanbanBoard = useStore($taskKanbanBoard);
    const taskKanbanBoardFormatted = useStore($taskKanbanBoardFormatted);
    const isTaskKanbanBoardLoading = useStore(fetchTaskKanbanBoard.pending);

    const [filterScope, setFilterScope] = useState<TaskKanbanFilterType>(TaskKanbanFilterType.ALL);
    const [selectedTeamIds, setSelectedTeamIds] = useState<ID[]>([]);
    const [selectedClientIds, setSelectedClientIds] = useState<ID[]>([]);

    const buildTaskKanbanFilterInput = (
        scope: TaskKanbanFilterType,
        teamIds: ID[],
        clientIds: ID[]
    ): TaskKanbanBoardFilterInput => {
        const input: TaskKanbanBoardFilterInput = {};

        if (scope === TaskKanbanFilterType.MY_TASKS && me) {
            input.participant_user_id = me.user.id;
        }

        if (teamIds.length > 0) {
            // Custom team selection overrides the implicit "my teams" IDs only while it is NOT empty
            input.team_id = teamIds;
        } else if (scope === TaskKanbanFilterType.MY_TEAMS && me) {
            input.team_id = me.team.map((t) => t.id);
        }

        if (clientIds.length > 0) {
            input.user_space_id = clientIds;
        }

        return input;
    };

    const handleRefetchTaskKanban = async () => {
        try {
            const taskKanbanBoard = await fetchTaskKanbanBoard(
                buildTaskKanbanFilterInput(filterScope, selectedTeamIds, selectedClientIds)
            );
            taskKanbanBoardFormattedApi.initialize({ taskKanbanBoard });
            handleStartTimeout();
        } catch (e) {
            // TODO: handle error
            console.error(e);
        }
    };
    const handleFetchTaskKanban = async (scope: TaskKanbanFilterType, teamIds: ID[], clientIds: ID[]) => {
        setFilterScope(scope);
        setSelectedTeamIds(teamIds);
        setSelectedClientIds(clientIds);

        try {
            const taskKanbanBoard = await fetchTaskKanbanBoard(buildTaskKanbanFilterInput(scope, teamIds, clientIds));
            taskKanbanBoardFormattedApi.initialize({ taskKanbanBoard });

            sessionStorage.setItem(
                SessionStorage.TASK_KANBAN_FILTER,
                serializeTaskKanbanFilter({ scope, team_ids: teamIds, client_ids: clientIds })
            );
        } catch (e) {
            // TODO: handle error
            console.error(e);
        }
    };

    useEffect(() => {
        // Get last applied filter, both the current value and the legacy plain scope string are accepted
        const lastFilter = parseStoredTaskKanbanFilter(sessionStorage.getItem(SessionStorage.TASK_KANBAN_FILTER));

        if (taskKanbanBoard) {
            if (lastFilter) {
                setFilterScope(lastFilter.scope);
                setSelectedTeamIds(lastFilter.team_ids);
                setSelectedClientIds(lastFilter.client_ids);
            }
        } else {
            // Initialize from persisted IDs when available; otherwise preserve the existing default scope
            const isMyTeamFilterAvailable =
                me && kanbanMyTeamFilterRoles.includes(me.user.role) && me.team.length > 0;
            const isMyTeamScopeDefault = isMyTeamFilterAvailable && kanbanMyTeamDefaultScopeRoles.includes(me.user.role);
            const defaultScope = isMyTeamScopeDefault ? TaskKanbanFilterType.MY_TEAMS : TaskKanbanFilterType.ALL;
            const initialScope =
                lastFilter?.scope === TaskKanbanFilterType.MY_TEAMS && !isMyTeamFilterAvailable
                    ? defaultScope
                    : lastFilter?.scope ?? defaultScope;

            handleFetchTaskKanban(
                initialScope,
                lastFilter?.team_ids ?? [],
                lastFilter?.client_ids ?? []
            );
        }
    }, []);

    if (!me) {
        return null;
    }

    const handleSelectAllFilter = () => {
        handleFetchTaskKanban(TaskKanbanFilterType.ALL, selectedTeamIds, selectedClientIds);
    };

    const handleSelectMyTaskFilter = () => {
        handleFetchTaskKanban(TaskKanbanFilterType.MY_TASKS, selectedTeamIds, selectedClientIds);
    };

    const handleSelectMyTeamFilter = () => {
        if (me.team.length === 0) {
            return;
        }

        handleFetchTaskKanban(TaskKanbanFilterType.MY_TEAMS, selectedTeamIds, selectedClientIds);
    };

    const handleChangeSelectedTeams = (teamIds: ID[]) => {
        handleFetchTaskKanban(filterScope, teamIds, selectedClientIds);
    };

    const handleChangeSelectedClients = (clientIds: ID[]) => {
        handleFetchTaskKanban(filterScope, selectedTeamIds, clientIds);
    };

    const renderKanbanFilter = () => {
        const isAllFilterActive = filterScope === TaskKanbanFilterType.ALL;
        const isMyTasksFilterActive = filterScope === TaskKanbanFilterType.MY_TASKS;
        const isMyTeamFilterActive = me.team.length > 0 && filterScope === TaskKanbanFilterType.MY_TEAMS;

        const isMyTeamFilterAvailable = me && kanbanMyTeamFilterRoles.includes(me.user.role) && me.team.length > 0;

        return (
            <Flex className="mb-3" direction="row" align="center" style={{ padding: '4px' }}>
                <DevText muted className="mr-1">
                    Show
                </DevText>

                <ButtonGroup>
                    <Button
                        disabled={isTaskKanbanBoardLoading}
                        active={isAllFilterActive}
                        onClick={handleSelectAllFilter}
                    >
                        All
                    </Button>
                    <Button
                        disabled={isTaskKanbanBoardLoading}
                        active={isMyTasksFilterActive}
                        onClick={handleSelectMyTaskFilter}
                    >
                        Assigned to me
                    </Button>
                    {isMyTeamFilterAvailable && (
                        <Button
                            disabled={isTaskKanbanBoardLoading}
                            active={isMyTeamFilterActive}
                            onClick={handleSelectMyTeamFilter}
                        >
                            Assigned to my team
                        </Button>
                    )}
                </ButtonGroup>

                <Flex className="ml-2" direction="row" align="center" gap="8px" flexWrap="wrap">
                    <TaskKanbanTeamsFilter
                        disabled={isTaskKanbanBoardLoading}
                        selectedTeamIds={selectedTeamIds}
                        onChange={handleChangeSelectedTeams}
                    />

                    <TaskKanbanClientsFilter
                        disabled={isTaskKanbanBoardLoading}
                        selectedClientIds={selectedClientIds}
                        onChange={handleChangeSelectedClients}
                    />
                </Flex>
            </Flex>
        );
    };

    const renderKanbanBoard = () => {
        // Task kanban board was NOT fetched yet
        if (!taskKanbanBoard || !taskKanbanBoardFormatted) {
            return (
                <Flex justify="center">
                    <Spinner />
                </Flex>
            );
        }

        // Task kanban board was fetched, but where are no tasks there
        if (taskKanbanBoard.tasks.length === 0) {
            return (
                <NonIdealState
                    icon={<Icon className="mb-2" icon="search" size={70} />}
                    title={
                        <Heading className="mb-1" type="h4">
                            No requests were found
                        </Heading>
                    }
                />
            );
        }

        const renderPausedTasksColumn = () => {
            return (
                <TaskKanbanColumn
                    title="Paused"
                    icon={<Icon className="mr-1" icon="pause" />}
                    color={Colors.ORANGE3}
                    taskKanbanBoardFormatted={taskKanbanBoardFormatted}
                    tasks={Object.values(taskKanbanBoardFormatted.tasks.paused)}
                />
            );
        };

        const renderBacklogTasksColumn = () => {
            return (
                <TaskKanbanColumn
                    title="Backlog"
                    icon={<Icon className="mr-1" icon="remove-row-bottom" />}
                    color={Colors.BLACK}
                    taskKanbanBoardFormatted={taskKanbanBoardFormatted}
                    tasks={Object.values(taskKanbanBoardFormatted.tasks[TaskQueue.BACKLOG])}
                />
            );
        };

        const renderTaskStateColumns = () => {
            return taskStateColumns.map((taskState) => {
                const { title, color } = taskStateInformation[taskState];

                return (
                    <TaskKanbanColumn
                        key={taskState}
                        title={title}
                        color={color}
                        taskKanbanBoardFormatted={taskKanbanBoardFormatted}
                        tasks={Object.values(taskKanbanBoardFormatted.tasks[taskState])}
                    />
                );
            });
        };

        const renderTaskStateColumnDivider = () => {
            return (
                <Flex fullHeight className="ml-3 mr-4" direction="row" align="stretch">
                    <Divider />
                </Flex>
            );
        };

        return (
            <ScrollContainerWrapper style={{ opacity: isTaskKanbanBoardLoading ? 0.5 : 1 }}>
                <StyledScrollContainer hideScrollbars={false} vertical={false} className="custom-thin-scroll">
                    {renderPausedTasksColumn()}
                    {renderBacklogTasksColumn()}
                    {renderTaskStateColumnDivider()}
                    {renderTaskStateColumns()}
                </StyledScrollContainer>
            </ScrollContainerWrapper>
        );
    };

    return (
        <PageContainer className="custom-thin-scroll">
            <Flex className="mb-2" direction="row">
                <Heading type="h3">Kanban board</Heading>
                <Button
                    minimal
                    loading={isTaskKanbanBoardLoading}
                    disabled={!taskKanbanBoard || isTaskKanbanBoardLoading || isTimeoutPresent}
                    className="ml-small"
                    icon="refresh"
                    onClick={handleRefetchTaskKanban}
                />
            </Flex>
            {renderKanbanFilter()}
            {renderKanbanBoard()}
        </PageContainer>
    );
};

export default TaskKanbanBoard;
